import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import LinearProgress from '@mui/material/LinearProgress';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {
  getPlannedModuleStory,
  listPlannedCourses,
  listPlannedLessons,
  updatePlannedModule,
} from '@platform/client';
import {
  MAX_LESSON_PAGE,
  STORY_HEADINGS,
  plannedCourseListSchema,
  plannedLessonListSchema,
  plannedModuleStorySchema,
} from '@platform/schema/curriculum';
import {
  MAX_STORY_FIGURES,
  MAX_WRITER_NOTES,
} from '@platform/schema/generation';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { describeApiError } from '../api-error';
import { castQuery } from '../authoring/cast';

import { castBody } from './cast';
import { castLine, ModuleCast } from './ModuleCast';
import { ModuleRecast } from './ModuleRecast';
import { StoryEditor } from './StoryEditor';

import type { MapLesson, SavedCast } from './ModuleCast';
import type { SavedStory } from './StoryEditor';
import type { UpdatePlannedModuleDto } from '@platform/client';
import type {
  CastAssignment,
  PlannedLesson,
  PlannedModuleStory,
} from '@platform/schema/curriculum';

export const STORY_HELPER =
  'Define the character, business, starting challenge, and how the story develops across this module’s lessons.';

const FIGURES_HELPER = `One per line, up to ${String(MAX_STORY_FIGURES)}: the price, cost, volume and other numbers every lesson starts from.`;

const NOTES_HELPER =
  'These apply to every lesson built or fixed in this module from now on.';

/** The card's five headings, prefilled in Create Module. */
export const STORY_TEMPLATE = STORY_HEADINGS.map(
  (heading) => `${heading}\n`,
).join('\n');

/** Read by Curriculum and by Library's module page, so the cached shape is one. */
export const plannedCoursesQuery = {
  queryKey: ['curriculum', 'courses'],
  queryFn: async () =>
    plannedCourseListSchema.parse(
      (await listPlannedCourses({ throwOnError: true })).data,
    ).courses,
};

const storyKey = (moduleId: string) => ['curriculum', 'module-story', moduleId];

async function fetchStory(moduleId: string) {
  return plannedModuleStorySchema.parse(
    (
      await getPlannedModuleStory({
        path: { id: moduleId },
        throwOnError: true,
      })
    ).data,
  );
}

const savedOf = (story: PlannedModuleStory): SavedStory => ({
  text: story.story,
  revision: story.storyRevision,
});

const savedFiguresOf = (story: PlannedModuleStory): SavedStory => ({
  text: story.figures.join('\n'),
  revision: story.storyRevision,
});

const savedNotesOf = (story: PlannedModuleStory): SavedStory => ({
  text: story.writerNotes,
  revision: story.storyRevision,
});

const figuresIn = (text: string): string[] =>
  text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line !== '');

const savedCastOf = (story: PlannedModuleStory): SavedCast => ({
  draft: {
    cast: story.cast,
    subsets: Object.fromEntries(
      story.lessons.map((one) => [one.lessonId, one.cast]),
    ),
  },
  revision: story.storyRevision,
});

/** Every planned lesson the list endpoint returns for a query, page by page. */
export async function plannedLessonsWhere(
  query: Omit<
    NonNullable<Parameters<typeof listPlannedLessons>[0]>['query'],
    'limit' | 'offset'
  >,
): Promise<PlannedLesson[]> {
  const lessons: PlannedLesson[] = [];
  for (let offset = 0; ; offset += MAX_LESSON_PAGE) {
    const page = plannedLessonListSchema.parse(
      (
        await listPlannedLessons({
          query: { ...query, limit: MAX_LESSON_PAGE, offset },
          throwOnError: true,
        })
      ).data,
    );
    lessons.push(...page.lessons);
    if (offset + MAX_LESSON_PAGE >= page.total) return lessons;
  }
}

function mapLessons(
  story: PlannedModuleStory,
  planned: readonly PlannedLesson[],
): MapLesson[] {
  const byId = new Map(planned.map((one) => [one.lessonId, one]));
  return story.lessons.map(({ lessonId, built }) => {
    const row = byId.get(lessonId);
    return {
      lessonId,
      built,
      name: row?.name ?? '',
      description: row?.description ?? '',
      storyBeat: row?.storyBeat ?? '',
      storyChanged: row?.storyChanged ?? false,
    };
  });
}

/** Opened from a module in the Curriculum table — CURR-39; its cast since CURR-43. */
export function ModuleStoryDialog({
  moduleId,
  onClose,
}: {
  moduleId: string | null;
  onClose: () => void;
}) {
  const story = useQuery({
    queryKey: storyKey(moduleId ?? ''),
    enabled: moduleId !== null,
    queryFn: () => fetchStory(moduleId ?? ''),
  });

  return (
    <Dialog open={moduleId !== null} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>
        Module story{story.data === undefined ? '' : ` — ${story.data.name}`}
      </DialogTitle>
      <DialogContent dividers>
        {story.isPending && <LinearProgress />}
        {story.isError && (
          <Alert severity="error">{describeApiError(story.error)}</Alert>
        )}
        {moduleId !== null && story.data !== undefined && (
          <ModuleStoryBody
            key={moduleId}
            moduleId={moduleId}
            story={story.data}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function ModuleStoryBody({
  moduleId,
  story,
}: {
  moduleId: string;
  story: PlannedModuleStory;
}) {
  const queries = useQueryClient();
  const people = useQuery(castQuery);
  const planned = useQuery({
    queryKey: ['curriculum', 'module-lessons', moduleId],
    queryFn: () => plannedLessonsWhere({ moduleId }),
  });
  // The story and the cast share one revision. A save from here moves it from r to r+1,
  // so the other editor's edit that started at r is rebased past it rather than refused
  // as stale against this author's own save. Anyone else's save still refuses.
  const ownSaves = useRef(new Map<number, number>());
  const [replacing, setReplacing] = useState<CastAssignment | null>(null);
  const patch = async (
    body: Omit<UpdatePlannedModuleDto, 'storyRevision'>,
    loaded: number,
  ): Promise<void> => {
    let revision = loaded;
    for (
      let next = ownSaves.current.get(revision);
      next !== undefined;
      next = ownSaves.current.get(revision)
    )
      revision = next;
    await updatePlannedModule({
      path: { id: moduleId },
      body: { ...body, storyRevision: revision },
      throwOnError: true,
    });
    ownSaves.current.set(revision, revision + 1);
    await queries.invalidateQueries({ queryKey: ['curriculum'] });
  };

  return (
    <Stack spacing={3}>
      <StoryEditor
        action="Save module story"
        label="Module story"
        helperText={STORY_HELPER}
        minRows={12}
        required
        saved={savedOf(story)}
        save={(text, revision) => patch({ story: text }, revision)}
        load={async () => savedOf(await fetchStory(moduleId))}
      />
      <StoryEditor
        action="Save case figures"
        label="Case figures"
        helperText={FIGURES_HELPER}
        minRows={4}
        saved={savedFiguresOf(story)}
        save={(text, revision) => patch({ figures: figuresIn(text) }, revision)}
        load={async () => savedFiguresOf(await fetchStory(moduleId))}
      />
      <StoryEditor
        action="Save notes for the writer"
        label="Notes for the writer"
        helperText={NOTES_HELPER}
        minRows={3}
        maxLength={MAX_WRITER_NOTES}
        saved={savedNotesOf(story)}
        save={(text, revision) => patch({ writerNotes: text }, revision)}
        load={async () => savedNotesOf(await fetchStory(moduleId))}
      />
      {(people.isPending || planned.isPending) && <LinearProgress />}
      {people.isError && (
        <Alert severity="error">{describeApiError(people.error)}</Alert>
      )}
      {planned.isError && (
        <Alert severity="error">{describeApiError(planned.error)}</Alert>
      )}
      {story.castProblem !== null && (
        <Alert severity="error" aria-label="Saved cast is invalid">
          The saved cast is invalid, so this module reads as casting nobody:{' '}
          {story.castProblem} Fix the cast below and save it.
        </Alert>
      )}
      {people.data !== undefined && (
        <ModuleRecast
          moduleId={moduleId}
          revision={story.storyRevision}
          cast={story.cast}
          people={people.data.cast}
          replacing={replacing}
          onStopReplacing={() => {
            setReplacing(null);
          }}
        />
      )}
      {people.data !== undefined && planned.data !== undefined && (
        <ModuleCast
          people={people.data.cast}
          onReplace={setReplacing}
          lessons={mapLessons(story, planned.data)}
          saved={savedCastOf(story)}
          save={({ cast, subsets }, revision) =>
            patch(
              {
                cast: castBody(cast),
                lessonCasts: Object.entries(subsets).map(([lessonId, ids]) => ({
                  lessonId,
                  cast: ids,
                })),
              },
              revision,
            )
          }
          load={async () => savedCastOf(await fetchStory(moduleId))}
        />
      )}
    </Stack>
  );
}

/**
 * Library's module page shows the story of the planned module linked to it, read-only:
 * the story lives in Curriculum, where the lessons are built from it.
 */
export function LinkedModuleStory({ collectionId }: { collectionId: string }) {
  const planned = useQuery({
    ...plannedCoursesQuery,
    select: (courses) =>
      courses
        .flatMap((course) => course.modules)
        .find((module) => module.collectionId === collectionId) ?? null,
  });
  const moduleId = planned.data?.id;
  const story = useQuery({
    queryKey: storyKey(moduleId ?? ''),
    enabled: moduleId !== undefined,
    queryFn: () => fetchStory(moduleId ?? ''),
  });
  const people = useQuery(castQuery);
  const names = new Map((people.data?.cast ?? []).map((one) => [one.id, one]));

  return (
    <Stack spacing={1} component="section" aria-label="Module story">
      <Typography variant="h6" component="h2">
        Module story
      </Typography>
      {(planned.isPending || (moduleId !== undefined && story.isPending)) && (
        <LinearProgress />
      )}
      {planned.isError && (
        <Alert severity="error">{describeApiError(planned.error)}</Alert>
      )}
      {story.isError && (
        <Alert severity="error">{describeApiError(story.error)}</Alert>
      )}
      {planned.data === null && (
        <Typography variant="body2" color="text.secondary">
          No Curriculum module is linked to this one, so it has no story.
        </Typography>
      )}
      {story.data !== undefined && (
        <>
          <Typography
            variant="body2"
            sx={{ whiteSpace: 'pre-wrap' }}
            color={story.data.story === '' ? 'text.secondary' : 'text.primary'}
          >
            {story.data.story === '' ? 'No story saved yet.' : story.data.story}
          </Typography>
          <Typography variant="subtitle2" component="h3">
            Characters
          </Typography>
          {story.data.cast.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No cast chosen yet.
            </Typography>
          ) : (
            <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2 }}>
              {story.data.cast.map((one) => (
                <Typography key={one.id} component="li" variant="body2">
                  {castLine(one, names)}
                </Typography>
              ))}
            </Stack>
          )}
          <Link
            component={RouterLink}
            to={`/curriculum?story=${encodeURIComponent(story.data.id)}`}
          >
            Edit the story in Curriculum
          </Link>
        </>
      )}
    </Stack>
  );
}
